import React, {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useCart } from "modules/cart/hooks/useCart";
import { useContext } from "react";
import { AppDataContext } from "modules/main/context/appData";
import { useNavigate } from "react-router-dom";
import { getTotalAmount, getTotalQty } from "utils/cartUtils";
import { toast } from "sonner";
import {
  Info,
  MilkOff,
  SearchX,
  ShoppingCart,
  Trash,
  WandSparkles,
} from "lucide-react";

import { Button } from "@/components/ui/button";

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetFooter,
} from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { sendViewCart } from "utils/analytics";
import { UserData } from "modules/authentication/types/user-data";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty";

import { useAuth } from "modules/authentication/hooks/use-auth";
import { useFetch } from "modules/main/hooks/useFetch";
import { AcquirePlusDialog } from "modules/subscription/components/AcquirePlusDialog";
import { dashboardEndPoint } from "modules/main/services/endpoints";
import { UserDashboard } from "modules/main/types/user-dashboard";
import { LoginDialog } from "modules/authentication/components/loginDialog";
import { CartItem, Payload } from "../types/cart";
import { StoreData } from "modules/main/types/storeData";
import { Card, CardContent } from "@/components/ui/card";
import { useRequireLocationForCart } from "modules/cart/hooks/useRequireLocationForCart";
import { LocationRequiredDialog } from "modules/main/components/location-required-dialog";
import MenuIcon from "modules/main/types/menus";
import posthog from "posthog-js";
import { ProductSearchPicker } from "modules/lists/components/product-search-picker";
import { MainFlowSteps } from "modules/lists/components/main-flow-steps";
import { ResolvedListItem, ListExecution } from "modules/lists/types/lists";
import { getListExecution } from "modules/lists/services/listService";
import { CartElement } from "./cart-element";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

import { Skeleton } from "@/components/ui/skeleton";

// import { useToast } from "@/hooks/use-toast";
// import { ChatContainer} from "modules/chat/chatContainer";

const MIN_OPTIMIZE_AMOUNT = 20000;

/** sessionStorage key for persisting the execution ID across page refreshes. */
const SESSION_STORAGE_EXECUTION_ID_KEY = "carriapp:executionId";

/** Prefix for sessionStorage key that tracks resolved missed item IDs per execution. */
const SESSION_STORAGE_RESOLVED_IDS_PREFIX = "carriapp:resolvedMissedIds";

const sendEventPH = (phEvent: "Optimizar") => {
  posthog.capture(phEvent);
};

const getUserData = (): UserData => {
  try {
    const data = localStorage.getItem("userData");
    if (!data) return {} as UserData;

    const parsed = JSON.parse(data) as UserData;
    return parsed;
  } catch (error) {
    console.warn("Error parsing userData from localStorage:", error);
    return {} as UserData;
  }
};

interface CartCategoryDefinition {
  id: number;
  name: string;
  icon: string;
  orderField: number;
}

interface CartCategoryGroup {
  category: CartCategoryDefinition;
  items: CartItem[];
}

const cartCategoryDefinitions: CartCategoryDefinition[] = [
  { id: 1, name: "Ofertas", icon: "sale", orderField: 0 },
  { id: 5, name: "Despensa", icon: "store", orderField: 10 },
  { id: 6, name: "Lácteos", icon: "milk", orderField: 20 },
  { id: 9, name: "Panadera, Pasteleria", icon: "bread", orderField: 25 },
  { id: 20, name: "Quesos y Fiambres", icon: "cheese", orderField: 27 },
  { id: 10, name: "Dulces y Colaciones", icon: "candy", orderField: 28 },
  { id: 3, name: "Frutas y Verduras", icon: "fruit", orderField: 30 },
  { id: 2, name: "Carniceria", icon: "beef", orderField: 40 },
  { id: 15, name: "Congelados", icon: "frozen", orderField: 45 },
  { id: 4, name: "Bebés", icon: "baby", orderField: 50 },
  { id: 7, name: "Limpieza", icon: "clean", orderField: 60 },
  { id: 8, name: "Perfumería", icon: "persCare", orderField: 65 },
  { id: 12, name: "Botilleria", icon: "drink", orderField: 70 },
  { id: 11, name: "Mascotas", icon: "house", orderField: 80 },
];

const fallbackCartCategory: CartCategoryDefinition = {
  id: -1,
  name: "Otros",
  icon: "store",
  orderField: Number.MAX_SAFE_INTEGER,
};

/**
 * Resolves the UI metadata for a cart category id.
 */
const getCartCategoryDefinition = (
  categoryId: number,
): CartCategoryDefinition => {
  return (
    cartCategoryDefinitions.find((category) => category.id === categoryId) ?? {
      ...fallbackCartCategory,
      id: categoryId,
    }
  );
};

/**
 * Reads the cart category id from the available item fields.
 */
const getCartItemCategoryId = (item: CartItem): number => {
  return item.category_id;
};

/**
 * Groups cart items by category and sorts groups using the configured display order.
 */
const groupCartItemsByCategory = (cart: CartItem[]): CartCategoryGroup[] => {
  const groupedItems = new Map<number, CartCategoryGroup>();

  cart.forEach((item) => {
    const categoryId = getCartItemCategoryId(item);
    const categoryDefinition = getCartCategoryDefinition(categoryId);
    const currentGroup = groupedItems.get(categoryId);

    if (currentGroup) {
      currentGroup.items.push(item);
      return;
    }

    groupedItems.set(categoryId, {
      category: categoryDefinition,
      items: [item],
    });
  });

  return Array.from(groupedItems.values()).sort((currentGroup, nextGroup) => {
    return currentGroup.category.orderField - nextGroup.category.orderField;
  });
};

export const CartSheet = ({
  open,
  setOpen,
}: {
  open?: boolean;
  setOpen?: (open: boolean) => void;
}) => {
  const { storeList } = useContext(AppDataContext);
  const {
    addDeletefromCart,
    removeFromCart,
    cart,
    clearCart,
    isInsertingItems,
    insertingCount,
    resolutionExecution,
    setResolutionMode,
    clearResolutionMode,
    isMigrating,
    migrateCart,
  } = useCart();
  const { isLogged, isPremiumUser } = useAuth();
  const navigate = useNavigate();
  const {
    isLocationDialogOpen,
    handleLocationSelected,
    handleLocationDialogOpenChange,
  } = useRequireLocationForCart();

  // Cut 3: proactive quota UX — the dashboard reports the free-plan monthly
  // optimization usage. At the limit the optimize CTA is disabled for
  // non-premium users (backend remains authoritative; this is UX only).
  const { data: dashboardData } = useFetch<UserDashboard>(dashboardEndPoint);
  const quotaUsage = dashboardData?.quotaUsage;
  const isAtMonthlyOptimizationLimit =
    !isPremiumUser &&
    quotaUsage !== undefined &&
    quotaUsage.monthlyOptimizationLimit - quotaUsage.monthlyOptimizations <= 0;

  const [isAcquirePlusOpen, setIsAcquirePlusOpen] = useState(false);

  // Guard for React Strict Mode — prevents duplicate migration calls
  const migrationRef = useRef(false);

  // Persist executionId to sessionStorage whenever resolutionExecution changes
  useEffect(() => {
    if (resolutionExecution?.id) {
      sessionStorage.setItem(
        SESSION_STORAGE_EXECUTION_ID_KEY,
        resolutionExecution.id,
      );
    }
  }, [resolutionExecution?.id]);

  // Recover missed items from stored executionId on mount (e.g. after refresh).
  // Guard: only applies the recovered execution if sessionStorage still has the
  // same ID (prevents stale overwrite when applyListExecution ran during recovery).
  useEffect(() => {
    const storedExecutionId = sessionStorage.getItem(
      SESSION_STORAGE_EXECUTION_ID_KEY,
    );
    if (storedExecutionId && !resolutionExecution) {
      getListExecution(storedExecutionId)
        .then((execution) => {
          if (
            sessionStorage.getItem(SESSION_STORAGE_EXECUTION_ID_KEY) ===
            storedExecutionId
          ) {
            setResolutionMode(execution);
          }
        })
        .catch(() => {
          sessionStorage.removeItem(SESSION_STORAGE_EXECUTION_ID_KEY);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const userData = getUserData();
    const cartVersion = localStorage.getItem("v") ?? "0";
    const userVersion = userData.v?.toString() ?? "";

    // Migration condition: user has version, cart differs from user version
    const needsMigration = userVersion !== "" && userVersion !== cartVersion;

    // Only trigger migration if cart has items, not already running, and not currently migrating
    if (
      needsMigration &&
      cart.length > 0 &&
      !migrationRef.current &&
      !isMigrating
    ) {
      migrationRef.current = true;
      migrateCart(Number(cartVersion), cart);
    }
  }, [cart, migrateCart, isLogged]);

  const totalAmount = getTotalAmount(cart);
  const isBelowOptimizeMinimum = totalAmount < MIN_OPTIMIZE_AMOUNT;
  const isOptimizeLoading = isInsertingItems;

  const handleOptimizeCart = () => {
    if (isBelowOptimizeMinimum) {
      toast.error(
        "El mínimo para optimizar es de $20.000. Agrega más productos.",
      );
      return;
    }

    // Cut 3: proactive quota gate — free users at the monthly optimization
    // limit see the upsell dialog instead of navigating to a blocked run.
    if (isAtMonthlyOptimizationLimit) {
      setIsAcquirePlusOpen(true);
      return;
    }

    sendEventPH("Optimizar");
    setOpen?.(false);
    navigate("/cart/calculate");
  };

  return (
    <Sheet {...(open !== undefined ? { open } : {})} onOpenChange={setOpen}>
      <SheetTrigger
        asChild
        onClick={() => {
          sendViewCart(cart);
        }}
      >
        <Button data-ph-event="view-cart" variant="link" size="default">
          <div className="relative inline-block">
            <ShoppingCart className="size-6 text-muted-foreground transition-colors hover:text-foreground" />
            {cart.length > 0 && (
              <span className="px-1 absolute min-w-4 w-auto h-4 left-4 top-0 rounded-full bg-red-500 lg:bg-orange-400 text-white text-[10px]">
                {getTotalQty(cart)}
              </span>
            )}
          </div>
        </Button>
      </SheetTrigger>
      <SheetContent className="flex flex-col w-full bg-gray-50 p-0 border-0 overflow-hidden gap-1">
        <SheetHeader className="pt-6 pb-0 px-6">
          <SheetTitle>
            <div className="flex items-center justify-between">
              <span>Tu Carrito </span>
              {/* <Button variant="ghost" size="sm" onClick={handleBulkUpdate} disabled={isUpdating || cart.length === 0}>
                                <ReloadIcon className={`inline-block mr-2 ${isUpdating ? "animate-spin" : ""}`}/>
                            </Button>                             */}
            </div>
          </SheetTitle>
          <SheetDescription></SheetDescription>
        </SheetHeader>
        {cart.length > 0 && (
          <MainFlowSteps
            currentStep={2}
            isExecuting={isInsertingItems}
            forceCompact
          />
        )}
        <div className="flex flex-1 min-h-0 flex-col px-6 overflow-hidden">
          <div className="flex-1 min-h-0 overflow-hidden">
            <CartContent
              cart={cart}
              storeList={storeList}
              addDeletefromCart={addDeletefromCart}
              removeFromCart={removeFromCart}
              clearCart={clearCart}
              isLogged={isLogged}
              insertingCount={insertingCount}
              setOpen={setOpen}
              isBelowOptimizeMinimum={isBelowOptimizeMinimum}
              resolutionExecution={resolutionExecution}
              clearResolutionMode={clearResolutionMode}
            />
          </div>
        </div>
        <SheetFooter className="flex sm:flex-col  flex-col w-full sm:justify-center bg-dark pb-6 px-6 pt-3 gap-4">
          {/* <CharContainer 
                        // openChatDrawer={openChatDrawer} 
                        // setOpenChatDrawer={setOpenChatDrawer}
                        /> */}
          <div className="flex flex-col w-full gap-2">
            {cart.length > 0 && (
              <div className="w-full flex flex-row justify-between items-center pt-2">
                {/* <div className="flex flex-col w-full gap-2  text-sm">
                                <div className="flex  gap-2 justify-between">
                                    <div className="font-semibold">PRODUCTOS:</div>
                                    <div className="font-bold min-w-14 text-right">{getTotalQty(cart)}</div>
                                </div>
                                <div className="flex justify-between items-center">
                                    <div className=" font-semibold text-left">Precio estimado:</div>
                                    <div className="font-bold min-w-14 text-right">
                                        <Amount
                                        amount={getTotalAmount(cart)}
                                        currency="CLP" locale="es-CL"
                                        />
                                    </div>
                                </div>                                  
                            </div> */}
                <div className="w-full flex justify-center text-base">
                  <Button
                    data-ph-event="optimize-cart"
                    className="mb-2 w-full font-semibold text-base"
                    size="lg"
                    onClick={handleOptimizeCart}
                    disabled={isOptimizeLoading || isBelowOptimizeMinimum}
                  >
                    {/* <WandSparkles className="h-5 w-5"/> */}
                    {isOptimizeLoading
                      ? "Actualizando carrito..."
                      : `Optimizar ${getTotalQty(cart)} productos`}
                  </Button>
                </div>
              </div>
            )}
            {/* <div className="text-xxs text-center flex flex-row items-center gap-2 "><Info className="w-4 h-4"/>Sujeto a mínimos de compra y costos de envío por tienda</div>                                                 */}
          </div>
        </SheetFooter>
      </SheetContent>
      <LocationRequiredDialog
        open={isLocationDialogOpen}
        onOpenChange={handleLocationDialogOpenChange}
        onLocationSelected={handleLocationSelected}
      />
      <AcquirePlusDialog
        open={isAcquirePlusOpen}
        onOpenChange={setIsAcquirePlusOpen}
        reason="monthly_optimization_limit"
        quota={{
          used: quotaUsage?.monthlyOptimizations ?? 0,
          limit: quotaUsage?.monthlyOptimizationLimit ?? 4,
        }}
      />
    </Sheet>
  );
};

// ── Inline types for MissedItemCard ────────────────────────────────────────────

interface MissedItemCardProps {
  item: ResolvedListItem;
  addDeletefromCart: (payload: Payload) => void;
  /** Called when the user resolves this missed item via the picker. */
  onResolved: (id: string) => void;
}

/**
 * View-only card for items that could not be matched during list execution.
 * Shows the original search query, a reason badge, and a "Ver catálogo" button
 * that opens ProductSearchPicker for manual replacement.
 *
 * On successful replacement, the parent (CartContent) removes the card
 * via the onResolved callback. The replacement preserves the original
 * quantity from the missed item.
 */
const MissedItemCard: React.FC<MissedItemCardProps> = ({
  item,
  addDeletefromCart,
  onResolved,
}) => {
  const [pickerOpen, setPickerOpen] = useState(false);

  const badgeLabel =
    item.missedReason === "linked_product_not_found"
      ? "Producto ya no disponible"
      : "Producto no encontrado en el catálogo";

  return (
    <Card
      className="text-sm w-full p-3 mt-1 overflow-hidden"
      data-testid="missed-item-card"
    >
      <CardContent className="flex flex-col gap-2 p-0 text-xs py-1 items-start">
        {/* Destructive-styled banner: "No encontramos un match para X" */}
        <div className="flex items-center w-full bg-destructive/10 text-destructive text-xxs font-medium rounded-sm px-2 py-2 max-w-full overflow-hidden">
          <div className="flex items-center gap-1 min-w-0 overflow-hidden">
            <SearchX className="shrink-0 size-3" />
            <span>
              No encontramos un match para{" "}
              <strong className="font-semibold">{item.rawDescription}</strong>
            </span>
          </div>
        </div>
        {/* Product-card layout — mimics CartElement pattern */}
        <div className="flex gap-3 text-xs min-w-0 overflow-hidden w-full mt-1">
          {/* Image/avatar area with MilkOff icon */}
          <div className="shrink-0">
            <div
              className="w-[60px] h-[60px] bg-muted rounded-md flex items-center justify-center"
              data-testid="missed-item-icon"
            >
              <MilkOff className="size-6 text-muted-foreground" />
            </div>
          </div>
          {/* Product info area */}
          <div className="flex flex-col items-start min-h-[60px] w-full gap-1 min-w-0">
            <p
              className="font-semibold text-sm min-w-0 overflow-hidden"
              data-testid="missed-item-name"
            >
              {item.rawDescription}
            </p>
            <span className="inline-flex items-center rounded-md border border-amber-200 bg-amber-50 px-2 py-0.5 text-xxs font-medium text-amber-700 whitespace-nowrap">
              {badgeLabel}
            </span>
            <div className="flex flex-row justify-end items-center w-full mt-1 gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPickerOpen(true)}
              >
                Ver catálogo
              </Button>
            </div>
          </div>
        </div>
        {pickerOpen && (
          <ProductSearchPicker
            open={pickerOpen}
            onOpenChange={setPickerOpen}
            initialQuery={item.rawDescription}
            onProductSelected={(payload) => {
              addDeletefromCart({ ...payload, quantity: item.quantity });
              onResolved(item.id);
            }}
          />
        )}
      </CardContent>
    </Card>
  );
};

interface CartContentProps extends React.HTMLAttributes<HTMLDivElement> {
  cart: CartItem[];
  storeList: StoreData[];
  addDeletefromCart: (payload: Payload) => void;
  removeFromCart: (payload: Payload) => void;
  clearCart: () => void;
  isLogged: boolean;
  /** Number of linked item insertions currently in flight — renders N skeleton placeholders. */
  insertingCount?: number;
  setOpen?: (open: boolean) => void;
  isBelowOptimizeMinimum: boolean;
  /** ListExecution containing missedItems for not-found card rendering. */
  resolutionExecution?: ListExecution | null;
  /** Clears resolution mode (called when all missed items are resolved). */
  clearResolutionMode?: () => void;
}

const CartContent = forwardRef<HTMLDivElement, CartContentProps>(
  (
    {
      cart,
      storeList,
      addDeletefromCart,
      removeFromCart,
      clearCart,
      isLogged,
      insertingCount = 0,
      setOpen,
      isBelowOptimizeMinimum,
      resolutionExecution,
      clearResolutionMode,
      ...props
    },
    ref,
  ) => {
    const pendingItems = cart.filter((i) => i.is_pending_resolution);
    const normalCart = cart.filter((i) => !i.is_pending_resolution);
    const cartGroups = groupCartItemsByCategory(normalCart);
    const executionId = resolutionExecution?.id;
    const missedItems = resolutionExecution?.missedItems ?? [];

    /**
     * Track which missed items have been resolved by the user via the picker.
     * Using a Set of IDs allows us to hide individual cards without local state
     * and makes the "Productos no encontrados" header disappear when all items
     * have been resolved.
     * IDs are persisted to sessionStorage keyed by executionId to survive
     * page refresh recovery (the recovery re-fetches the execution from the
     * backend, and this rehydrates the local resolved state).
     */
    const [resolvedIds, setResolvedIds] = useState<Set<string>>(new Set());

    // Rehydrate resolved IDs from sessionStorage when executionId changes
    // (e.g. after the refresh recovery useEffect fires in CartSheet).
    useEffect(() => {
      if (!executionId) {
        setResolvedIds(new Set());
        return;
      }
      try {
        const stored = sessionStorage.getItem(
          `${SESSION_STORAGE_RESOLVED_IDS_PREFIX}:${executionId}`,
        );
        setResolvedIds(stored ? new Set(JSON.parse(stored)) : new Set());
      } catch {
        setResolvedIds(new Set());
      }
    }, [executionId]);

    // Persist resolved IDs to sessionStorage whenever they change
    useEffect(() => {
      if (!executionId) return;
      try {
        sessionStorage.setItem(
          `${SESSION_STORAGE_RESOLVED_IDS_PREFIX}:${executionId}`,
          JSON.stringify(Array.from(resolvedIds)),
        );
      } catch {
        // sessionStorage full or unavailable — silently ignore
      }
    }, [resolvedIds, executionId]);

    // When all missed items for the current execution are resolved, clean up
    // resolution mode and remove storage keys.
    useEffect(() => {
      if (!executionId) return;
      if (missedItems.length > 0 && resolvedIds.size >= missedItems.length) {
        sessionStorage.removeItem(SESSION_STORAGE_EXECUTION_ID_KEY);
        sessionStorage.removeItem(
          `${SESSION_STORAGE_RESOLVED_IDS_PREFIX}:${executionId}`,
        );
        clearResolutionMode?.();
      }
    }, [
      executionId,
      missedItems.length,
      resolvedIds.size,
      clearResolutionMode,
    ]);

    const unresolvedMissedItems = missedItems.filter(
      (item) => !resolvedIds.has(item.id),
    );
    const handleMissedItemResolved = useCallback((id: string) => {
      setResolvedIds((prev) => new Set(prev).add(id));
    }, []);

    return (
      <ScrollArea
        type="scroll"
        className="h-full w-full flex-1 overflow-hidden"
      >
        {cart.length === 0 && unresolvedMissedItems.length === 0 ? (
          <>
            <Empty className="my-20">
              <EmptyHeader>
                <EmptyMedia variant="icon">
                  <ShoppingCart />
                </EmptyMedia>
                <EmptyTitle>Tu Carrito está vacío</EmptyTitle>
                <EmptyDescription>
                  {isLogged
                    ? "Aún no has agregado productos. Empieza tu compra y te mostraremos cómo optimizar precios entre tiendas."
                    : "Inicia sesión para comenzar a agregar productos a tu carrito y optimizar tus compras."}
                </EmptyDescription>
                {!isLogged && (
                  <EmptyContent>
                    <LoginDialog />
                  </EmptyContent>
                )}
              </EmptyHeader>
            </Empty>
          </>
        ) : (
          <>
            {/* ── Cart hints ──────────────────────────────────────────────── */}
            {isBelowOptimizeMinimum && (
              <div className="flex items-start gap-2 rounded-md bg-orange-50 px-3 py-2 text-xs text-muted-foreground font-medium">
                <Info className="mt-0.5 h-4 w-4 shrink-0 text-orange-600" />
                <span>
                  Tu carrito aún es muy pequeño. Para comenzar a ver ahorros en
                  la optimización el mínimo es de $20.000.
                </span>
              </div>
            )}
            {/* ── Pending resolution section (unresolved items) ───── */}
            {pendingItems.length > 0 && (
              <div className="mb-4">
                <div className="mb-2 flex items-center gap-2 rounded-md bg-primary/10 px-3 py-2 text-xs font-semibold text-primary">
                  <WandSparkles className="h-4 w-4" />
                  <span>Productospendientes...</span>
                </div>
                <div className="space-y-2">
                  {pendingItems.map((item) => (
                    <div
                      key={item.id}
                      className="rounded-lg border bg-card p-3 space-y-2"
                    >
                      <Skeleton className="h-4 w-3/4" />
                      <Skeleton className="h-3 w-1/2" />
                    </div>
                  ))}
                </div>
              </div>
            )}
            {/* ── Missed items section (not found products) ───────────────── */}
            {unresolvedMissedItems.length > 0 && (
              <div className="mb-4">
                <div className="mb-2 flex items-center gap-2 rounded-md bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-700">
                  <SearchX className="h-4 w-4" />
                  <span>Productos no encontrados</span>
                </div>
                <div className="space-y-2">
                  {unresolvedMissedItems.map((item) => (
                    <MissedItemCard
                      key={item.id}
                      item={item}
                      addDeletefromCart={addDeletefromCart}
                      onResolved={handleMissedItemResolved}
                    />
                  ))}
                </div>
              </div>
            )}
            {/* ── Regular cart items ────────────────────────────────────── */}
            {cartGroups.map((group) => {
              return (
                <div
                  key={group.category.id}
                  className="mt-4 first:mt-0 w-full overflow-hidden"
                >
                  <div className="mb-2 flex items-center gap-2 rounded-md bg-white/80 px-3 py-2 text-xs font-semibold text-foreground shadow-sm ring-1 ring-border/60">
                    <MenuIcon
                      iconName={group.category.icon}
                      size={16}
                      className="text-primary"
                    />
                    <span>{group.category.name}</span>
                  </div>
                  <div className="space-y-1 w-full overflow-hidden">
                    {group.items.map((item) => {
                      return (
                        <CartElement
                          key={item.id}
                          item={item}
                          cart={cart}
                          storeList={storeList}
                          addDeletefromCart={addDeletefromCart}
                          removeFromCart={removeFromCart}
                        />
                      );
                    })}
                  </div>
                </div>
              );
            })}
            {/* ── In-flight insertion skeletons ─────────────────────────── */}
            {insertingCount > 0 && (
              <div className="mt-4 space-y-1" data-testid="inserting-skeletons">
                {Array.from({ length: insertingCount }).map((_, i) => (
                  <div
                    key={`inserting-skeleton-${i}`}
                    className="rounded-lg border bg-card p-3 space-y-2"
                  >
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-3 w-1/2" />
                  </div>
                ))}
              </div>
            )}
            <div className="w-full flex justify-center">
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button size="sm" variant="destructive" className="mt-6">
                    <Trash className="" />
                    Vaciar Carrito
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>
                      ¿Quieres descartar todos los productos del carrito?
                    </AlertDialogTitle>
                    <AlertDialogDescription></AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => {
                        clearCart();
                      }}
                    >
                      Vaciar
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </>
        )}
      </ScrollArea>
    );
  },
);
